import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import {
  Dialog,
  // DialogContent,
  // DialogContentText,
  // Container,
  // DialogTitle,
  // DialogActions,
  Typography,
  Grid,
  Button,
  Slide,
  AlertTitle,
  Alert,
} from '@mui/material';

import { makeStyles } from '@mui/styles';
import { sourceTextFromData } from '../../../utils/functions';
import {
  useRouter,
  useGTMDataLayer,
  useVariables,
  useFeatureToggle,
} from '../../../hooks';
import { statusOk } from '../../../utils/variables';
import styles from './style';
import {
  serverModalErrorEvent,
  wizardsServerErrorEvent,
} from '../../../utils/variables/events';

const useStyles = makeStyles(styles);

const renderHtmlMessage = (storeState, message) => {
  const text = sourceTextFromData(storeState, message);
  if (!text) return null;
  if (typeof text !== 'string') return text;
  return <span dangerouslySetInnerHTML={{ __html: text }} />;
};

// Minimal wizard alert (e.g. term-quote sum-insured notice): put the second
// sentence on its own row with a blank line between the two so the message is
// friendlier to read. Only the first sentence boundary (". ") is broken, so
// amounts like "110.00 AWG" are left untouched.
const renderWizardHtmlMessage = (storeState, message) => {
  const text = sourceTextFromData(storeState, message);
  if (!text) return null;
  if (typeof text !== 'string') return text;
  const spacedText = text.replace(/\.\s+/, '.<br /><br />');
  return <span dangerouslySetInnerHTML={{ __html: spacedText }} />;
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const hasCrmCaseNumber = responseData => Boolean(responseData?.details?.crmCaseNumber);

const getErrorMessages = (responseData, { contactNumber, storeState, useFatumLogin }) => {
  const statusCode =
    responseData?.status || responseData?.statusCode || responseData?.responseDesc?.code;
  let stringErrorMessage = '';
  let defaultErrorTitle =
    'We’re sorry, but your signup attempt couldn’t be completed at the moment.';
  let defaultErrorMessage = `Please try again, or contact our customer support at ${contactNumber}`;
  let defaultAlertColor = '';
  let defaultRequestId = '';

  const isMultiple = Array.isArray(responseData);

  if (statusCode === 429) {
    defaultErrorTitle = 'Too many requests';
    stringErrorMessage = responseData?.message;
    defaultAlertColor = 'error';
  }
  const errors = [];
  if (useFatumLogin && hasCrmCaseNumber(responseData)) {
    errors.push({
      title: defaultErrorTitle,
      message: `A support case has been created for you with the following reference number: <strong>${responseData?.details?.crmCaseNumber}</strong>.Our support team is already looking into it and will contact you as soon as possible.If you have any questions or need help in the meantime, feel free to contact us at <a href="mailto:GGF_Customer_Support@myguardiangroup.com">GGF_Customer_Support@myguardiangroup.com</a>.Please mention your case number in your message so we can assist you faster.`,
      footer: 'Thank you for your patiencen and understanding.',
      type: responseData?.responseDesc?.type || defaultAlertColor,
      requestId: defaultRequestId,
      redirectUrl: responseData?.responseDesc?.redirectUrl,
    });
    return errors;
  }
  if (!isMultiple && responseData?.responseType) {
    defaultErrorTitle =
      responseData?.responseType === statusOk
        ? 'Success'
        : 'We’re sorry, but your signup attempt couldn’t be completed at the moment.';
    defaultErrorMessage =
      responseData?.responseType === statusOk
        ? 'Everything works fine'
        : 'Please check your internet connection, and try again.';
    defaultAlertColor = responseData?.responseType === statusOk ? 'success' : 'error';
    defaultRequestId = responseData?.headers?.['x-request-id'] || statusCode;
    const error =
      responseData?.responseDesc?.message?.message ||
      responseData?.responseDesc?.message ||
      responseData?.responseDesc ||
      defaultErrorMessage;
    stringErrorMessage =
      typeof error === 'object' && error !== null
        ? `${JSON.stringify(error, null, '\t')}`
        : sourceTextFromData(storeState, error);

    errors.push({
      title: responseData?.responseDesc?.title || defaultErrorTitle,
      message: stringErrorMessage,
      type: responseData?.responseDesc?.type || defaultAlertColor,
      requestId: defaultRequestId,
      redirectUrl: responseData?.responseDesc?.redirectUrl,
    });
  } else if (isMultiple && responseData) {
    responseData.forEach((response, index) => {
      const defaultMultiErrorTitle =
        response?.responseType === statusOk
          ? 'Success'
          : 'We’re sorry, but your signup attempt couldn’t be completed at the moment.';
      const defaultMultiAlertColor =
        response?.responseType === statusOk ? 'success' : 'error';
      const subError =
        response?.responseDesc?.message?.message ||
        response?.responseDesc?.message ||
        response?.responseDesc ||
        defaultErrorMessage;
      const subStringErrorMessage =
        typeof subError === 'object' && subError !== null
          ? `${JSON.stringify(subError, null, '\t')}`
          : subError;
      const subRequestId = response?.headers?.['x-request-id'];
      errors.push({
        title: response?.responseDesc?.title || defaultMultiErrorTitle,
        message: subStringErrorMessage,
        type: response?.responseDesc?.type || defaultMultiAlertColor,
        requestId: subRequestId,
        redirectUrl: response?.responseDesc?.redirectUrl,
      });
    });
  } else {
    defaultRequestId = responseData?.headers?.['x-request-id'] || statusCode;
    errors.push({
      title: defaultErrorTitle,
      message: stringErrorMessage,
      type: defaultAlertColor,
      requestId: defaultRequestId,
      redirectUrl: responseData?.responseDesc?.redirectUrl,
    });
  }

  return errors;
};

const ServerErrorModal = ({
  open: initialOpen,
  closeDialog,
  responseData,
  redirectPath,
  isWizardError,
  slug,
  errorModalConfig,
}) => {
  // console.log('responseData: ', responseData);
  const classes = useStyles();
  // const dispatch = useDispatch();
  const router = useRouter();
  const storeGtmDataLayer = useGTMDataLayer();
  const storeState = useSelector(state => state);
  const variables = useVariables();
  const doFeatureToggle = useFeatureToggle();
  const useFatumLogin = doFeatureToggle('useFatumLogin');
  const isCrmCase = useFatumLogin && hasCrmCaseNumber(responseData);
  const contactNumber = variables?.contactNumbers?.contact_us?.phone;
  const useMinimalWizardErrorStyle =
    isWizardError && (errorModalConfig?.whiteBackground || errorModalConfig?.hideTitle);

  const [errors, setErrors] = useState(null);
  const [open, setOpen] = useState(initialOpen);

  useEffect(() => {
    const newErrors = getErrorMessages(responseData, {
      contactNumber,
      storeState,
      useFatumLogin,
    });
    setOpen(initialOpen);
    setErrors(newErrors);
    // console.log('newErrors: ', newErrors);
    if (newErrors && newErrors?.length > 0) {
      newErrors?.forEach((error, index) => {
        if (error?.message) {
          storeGtmDataLayer({
            event: isWizardError ? wizardsServerErrorEvent : serverModalErrorEvent,
            serverErrorType: error?.type,
            serverErrorMsg: error?.message,
            slug: isWizardError ? slug : router.asPath,
          });
        }
      });
    }
  }, [responseData]);

  const handleAction = error => {
    setOpen(false);
    const redirectUrl = error?.redirectUrl;
    closeDialog();
    if (isCrmCase) {
      router.replace(redirectPath);
    } else if (redirectUrl) {
      router.push(redirectUrl);
    }
  };

  const actionLabel = isCrmCase ? 'Understood' : 'Close';

  const responseDisplay = (
    <div className={classes.root}>
      {errors?.map((error, index) => {
        if (useMinimalWizardErrorStyle) {
          return (
            <Grid
              key={`wizard-error-${index}`}
              container
              alignItems="center"
              justifyContent="center"
              className={classes.wizardErrorPaper}
            >
              {error?.message && (
                <Typography
                  variant="body1"
                  align="left"
                  className={classes.wizardErrorMessage}
                >
                  {renderWizardHtmlMessage(storeState, error?.message)}
                </Typography>
              )}
              {error?.footer && (
                <Typography
                  variant="body1"
                  align="left"
                  className={classes.wizardErrorMessage}
                >
                  {renderHtmlMessage(storeState, error?.footer)}
                </Typography>
              )}
              {error && (
                <Button color="primary" onClick={() => handleAction(error)}>
                  {actionLabel}
                </Button>
              )}
            </Grid>
          );
        }

        return (
          <Alert
            key={`server-error-${index}`}
            severity={error?.type}
            icon={errorModalConfig?.hideIcon ? false : undefined}
            className={classes.alert}
            classes={{ root: classes.align, icon: classes.icon }}
          >
            <Grid container alignItems="center" justifyContent="center">
              {!errorModalConfig?.hideTitle && (
                <AlertTitle>
                  <Typography variant="h5" align="center" className={classes.text}>
                    {error?.title}
                  </Typography>
                </AlertTitle>
              )}
              {error?.message && (
                <Typography variant="body1" align="center" className={classes.text}>
                  {renderHtmlMessage(storeState, error?.message)}
                </Typography>
              )}
              {error?.footer && (
                <Typography variant="body1" align="center" className={classes.text}>
                  {renderHtmlMessage(storeState, error?.footer)}
                </Typography>
              )}
              {error &&
                (isCrmCase ? (
                  <Grid item xs={12} className={classes.crmActionContainer}>
                    <Button
                      size="small"
                      className={classnames(classes.primaryButton, classes.crmAction)}
                      onClick={() => handleAction(error)}
                    >
                      {actionLabel}
                    </Button>
                  </Grid>
                ) : (
                  <Button onClick={() => handleAction(error)}>{actionLabel}</Button>
                ))}
              <br />
              {error?.requestId && !errorModalConfig?.hideRequestId && (
                <Grid item xs={12} align="left">
                  <Typography
                    variant="caption"
                    align="left"
                    component="p"
                    className={classes.requestId}
                    sx={{ fontSize: '10px' }}
                  >
                    Request: {error?.requestId}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Alert>
        );
      })}
    </div>
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      disableEscapeKeyDown={isCrmCase}
      // onClose={closeDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      scroll="body"
      PaperProps={{
        elevation: useMinimalWizardErrorStyle ? 8 : 0,
        sx: useMinimalWizardErrorStyle
          ? { backgroundColor: '#ffffff', borderRadius: 2 }
          : undefined,
      }}
    // {classes.responseData?.responseDesc?.title || classes.error}
    >
      {responseDisplay}
    </Dialog>
  );
};

ServerErrorModal.propTypes = {
  closeDialog: PropTypes.func,
  responseData: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  open: PropTypes.bool,
  alertColor: PropTypes.oneOf(['error', 'warning', 'info', 'success']),
  redirectPath: PropTypes.string,
  isMultiple: PropTypes.bool,
  isWizardError: PropTypes.bool,
  slug: PropTypes.string,
  errorModalConfig: PropTypes.shape({
    hideTitle: PropTypes.bool,
    whiteBackground: PropTypes.bool,
    hideIcon: PropTypes.bool,
    hideRequestId: PropTypes.bool,
  }),
};

ServerErrorModal.defaultProps = {
  closeDialog: () => { },
  responseData: [],
  open: false,
  alertColor: 'error',
  redirectPath: '/',
  isMultiple: false,
  isWizardError: false,
  slug: '',
  errorModalConfig: null,
};

export default ServerErrorModal;
